import { Form } from "@unform/web";
import { Building2, Pencil, Users, X } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import Preview from "~/components/Preview";
import Input from "~/components/RegisterForm/Input";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import FormLoading from "~/components/RegisterForm/FormLoading";
import FindGeneric from "~/components/Finds/FindGeneric";
import { FullGridContext } from "../../..";
import { getRegistries, handleUpdatedFields } from "~/functions";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import { Zoom, toast } from "react-toastify";
import api from "~/services/api";

export const InputContext = createContext({});

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const auth = useSelector((state) => state.auth);
  const { handleOpened, setOpened, successfullyUpdated, setSuccessfullyUpdated } =
    useContext(FullGridContext);
  const [pageData, setPageData] = useState({
    name: "",
    filial: null,
    filial_id: null,
    supervisor_id: null,
    supervisor: null,
    staffs: [],
    loaded: false,
  });
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const generalForm = useRef();
  const [staffOptions, setStaffOptions] = useState([]);

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  function handleGeneralFormSubmit(data) {
    const supervisorId =
      typeof data.supervisor_id !== "undefined" && data.supervisor_id !== ""
        ? data.supervisor_id
        : pageData.supervisor_id || null;
    const payload = {
      name: data.name,
      filial: data.filial?.id ? { id: data.filial.id } : null,
      supervisor_id: supervisorId,
    };

    const selectedFilial =
      data.filial && data.filial.id
        ? { id: data.filial.id, name: data.filial.name }
        : pageData.filial;
    const selectedSupervisor =
      staffOptions.find((opt) => String(opt.value) === String(payload.supervisor_id)) ||
      (data.supervisor_id && pageData.supervisor_id === data.supervisor_id
        ? { value: pageData.supervisor_id, label: pageData.supervisor?.name }
        : null);

    if (successfullyUpdated) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      return;
    }

    if (id === "new") {
      api
        .post(`/departments`, payload)
        .then((response) => {
          const newId = response.data?.id || null;
          setOpened(newId);
          setPageData({
            ...pageData,
            ...payload,
            filial: selectedFilial,
            filial_id: selectedFilial?.id || null,
            supervisor_id: payload.supervisor_id,
            supervisor: selectedSupervisor
              ? { id: selectedSupervisor.value, name: selectedSupervisor.label }
              : null,
          });
          setSuccessfullyUpdated(true);
          toast("Saved!", { autoClose: 1000 });
          handleOpened(null);
        })
        .catch((err) => {
          if (err?.response?.data?.error) {
            toast(err.response.data.error, { type: "error", autoClose: 3000 });
          }
        });
    } else if (id !== "new") {
      const updated = handleUpdatedFields(
        { ...payload, filial: selectedFilial },
        pageData
      );

      if (updated.length > 0) {
        const objUpdated = Object.fromEntries(updated);
        const updatePayload = {
          ...objUpdated,
          name: payload.name || pageData.name,
          filial: selectedFilial,
          supervisor_id: payload.supervisor_id,
        };
        api
          .put(`/departments/${id}`, updatePayload)
          .then(() => {
            setPageData({
              ...pageData,
              ...updatePayload,
              filial: selectedFilial,
              supervisor_id: payload.supervisor_id,
              supervisor: selectedSupervisor
                ? { id: selectedSupervisor.value, name: selectedSupervisor.label }
                : null,
            });
            setSuccessfullyUpdated(true);
            toast("Saved!", { autoClose: 1000 });
            handleOpened(null);
          })
          .catch((err) => {
            if (err?.response?.data?.error) {
              toast(err.response.data.error, { type: "error", autoClose: 3000 });
            }
          });
      }
    }
  }

  useEffect(() => {
    if (id === "new") {
      setFormType("full");
      setPageData((prev) => ({ ...prev, loaded: true }));
    } else if (id) {
      async function fetchDepartment() {
        try {
          const { data } = await api.get(`/departments/${id}`);
          const {
            created_by,
            created_at,
            updated_by,
            updated_at,
            canceled_by,
            canceled_at,
          } = data;
          const registries = await getRegistries({
            created_by,
            created_at,
            updated_by,
            updated_at,
            canceled_by,
            canceled_at,
          });
          setRegistry(registries);
          setPageData({
            ...data,
            filial: data.filial || null,
            filial_id: data.filial?.id || null,
            supervisor_id: data.supervisor_id || data.supervisor?.id || null,
            supervisor: data.supervisor || null,
            staffs: data.staffs || [],
            loaded: true,
          });
        } catch (err) {
          if (err?.response?.data?.error) {
            toast(err.response.data.error, { type: "error", autoClose: 3000 });
          }
        }
      }
      fetchDepartment();
    }
  }, [id]);

  useEffect(() => {
    async function loadStaffs() {
      try {
        const { data } = await api.get("/staffs-simple");
        const options =
          data?.map((staff) => ({
            value: staff.id,
            label: staff.name,
          })) || [];
        setStaffOptions(options);
      } catch (err) {
        console.error("Error loading staffs:", err);
      }
    }
    loadStaffs();
  }, []);

  const defaultFilialValue =
    id === "new" && auth.filial?.id && auth.filial.id !== 1
      ? { id: auth.filial.id, name: auth.filial.name }
      : pageData.filial
      ? { id: pageData.filial.id, name: pageData.filial.name }
      : { id: null, name: "" };

  const defaultSupervisorValue =
    staffOptions.find(
      (opt) => String(opt.value) === String(pageData.supervisor_id)
    ) ||
    (pageData.supervisor_id && pageData.supervisor
      ? { value: pageData.supervisor_id, label: pageData.supervisor.name }
      : null);

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        formType === "preview" ? (
          <div className="border h-full rounded-xl overflow-hidden flex flex-col justify-start gap-1 overflow-y-scroll">
            <div className="relative bg-gray-100 h-16 px-4 py-2 flex flex-row items-start justify-start">
              <button
                onClick={() => setFormType("full")}
                className="absolute top-2 right-20 text-md font-bold bg-mila_orange text-white rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <Pencil size={16} color="#fff" /> Open
              </button>
              <button
                onClick={() => handleOpened(null)}
                className="absolute top-2 right-2 text-md font-bold bg-secondary rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <X size={16} /> Close
              </button>
              <h2 style={{ fontSize: 24 }}>{pageData.name}</h2>
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-row items-start justify-between gap-4">
            <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="general"
              >
                <Building2 size={16} /> General
              </RegisterFormMenu>
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="staffs"
                disabled={id === "new"}
                messageOnDisabled="You need to save the department first"
              >
                <Users size={16} /> Staffs
              </RegisterFormMenu>
            </div>
            <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
              <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full"
                >
                  <InputContext.Provider
                    value={{
                      id,
                      generalForm,
                      setSuccessfullyUpdated,
                      fullscreen,
                      setFullscreen,
                      successfullyUpdated,
                      handleCloseForm,
                    }}
                  >
                    {id === "new" || pageData.loaded ? (
                      <>
                        <FormHeader
                          access={access}
                          title={pageData.name || "New Department"}
                          registry={registry}
                          InputContext={InputContext}
                          createText="Save Department"
                        />

                        <InputLineGroup
                          title="GENERAL"
                          activeMenu={activeMenu === "general"}
                        >
                          <FindGeneric
                            route="filials"
                            title="Filial"
                            scope="filial"
                            required
                            InputContext={InputContext}
                            defaultValue={defaultFilialValue}
                            fields={[
                              {
                                title: "Name",
                                name: "name",
                              },
                            ]}
                          />

                          <InputLine title="Department Information">
                            <Input
                              type="text"
                              name="name"
                              required
                              title="Name"
                              grow
                              defaultValue={pageData.name}
                              InputContext={InputContext}
                            />
                            <SelectPopover
                              name="supervisor_id"
                              grow
                              title="Supervisor"
                              isSearchable
                              isClearable
                              options={staffOptions}
                              defaultValue={defaultSupervisorValue}
                              value={defaultSupervisorValue}
                              placeholder="Select a supervisor"
                              InputContext={InputContext}
                              setReturnPopover={(opt) =>
                                setPageData((prev) => ({
                                  ...prev,
                                  supervisor_id: opt?.value || null,
                                }))
                              }
                            />
                          </InputLine>
                        </InputLineGroup>

                        <InputLineGroup
                          title="staffs"
                          activeMenu={activeMenu === "staffs"}
                        >
                          <InputLine title="Department Staffs">
                            <div className="flex flex-col justify-start items-start w-full gap-2">
                              {pageData.staffs && pageData.staffs.length > 0 ? (
                                pageData.staffs.map((staff) => (
                                  <div
                                    key={staff.id}
                                    className="flex flex-row items-center justify-between w-full border rounded-lg px-4 py-2 bg-gray-50 text-sm"
                                  >
                                    <span className="font-medium text-gray-800">
                                      {staff.name} {staff.last_name}
                                    </span>
                                    <span className="text-gray-500">
                                      {staff.email}
                                    </span>
                                  </div>
                                ))
                              ) : (
                                <p className="text-gray-400 text-sm py-2">
                                  No staffs assigned to this department.
                                </p>
                              )}
                            </div>
                          </InputLine>
                        </InputLineGroup>
                      </>
                    ) : (
                      <FormLoading />
                    )}
                  </InputContext.Provider>
                </Form>
              </div>
            </div>
          </div>
        )
      ) : null}
    </Preview>
  );
}
